/**
 * Der Live-Platzplan zeigt den Hintergrund als <img>. Ein SVG wird dabei bei
 * jedem Zoom und Verschieben neu gerastert und blockiert die Seite. Deshalb
 * wird es einmal in ein PNG umgewandelt; die Ansicht selbst bleibt ein Bitmap.
 */

const SVG_METADATA_RE = /<metadata\b[^>]*>[\s\S]*?<\/metadata>/gi;

export function isSvgPlanImage(value: string): boolean {
  const head = value.trimStart().slice(0, 240).toLowerCase();
  return head.startsWith("<svg") || head.startsWith("<?xml") || head.startsWith("data:image/svg+xml");
}

/** Längste Kante der Anzeige-Bitmap. Scharf genug zum Zoomen, klein genug zum Verschieben. */
export function planRasterMaxEdge(): number {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  return Math.round(Math.min(4096, Math.max(2048, 1800 * dpr)));
}

function svgMarkupForRaster(markup: string, maxEdge: number): string {
  const withoutMeta = markup.replace(SVG_METADATA_RE, "");
  return withoutMeta.replace(/<svg\b([^>]*)>/i, (_match, attrs: string) => {
    const cleaned = attrs
      .replace(/\swidth="[^"]*"/gi, "")
      .replace(/\sheight="[^"]*"/gi, "")
      .replace(/\swidth='[^']*'/gi, "")
      .replace(/\sheight='[^']*'/gi, "");
    const box = /viewBox=["']([^"']+)["']/i.exec(cleaned);
    let width = maxEdge;
    let height = maxEdge;
    if (box?.[1]) {
      const parts = box[1].trim().split(/[\s,]+/).map(Number);
      const viewWidth = parts[2] ?? 0;
      const viewHeight = parts[3] ?? 0;
      if (viewWidth > 0 && viewHeight > 0) {
        const scale = maxEdge / Math.max(viewWidth, viewHeight);
        width = Math.max(1, Math.round(viewWidth * scale));
        height = Math.max(1, Math.round(viewHeight * scale));
      }
    }
    return `<svg${cleaned} width="${width}" height="${height}">`;
  });
}

async function svgTextFromSource(imageData: string): Promise<string> {
  if (!imageData.startsWith("data:")) return imageData;
  const blob = await (await fetch(imageData)).blob();
  return blob.text();
}

function canvasToPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("PNG"))), "image/png");
  });
}

async function drawSvgBlob(blob: Blob): Promise<Blob> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(blob);
      try {
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, bitmap.width);
        canvas.height = Math.max(1, bitmap.height);
        const ctx = canvas.getContext("2d", { alpha: false });
        if (!ctx) throw new Error("ctx");
        ctx.drawImage(bitmap, 0, 0);
        return await canvasToPng(canvas);
      } finally {
        bitmap.close();
      }
    } catch {
      /* ältere Browser bzw. SVG ohne Maße: über <img> */
    }
  }

  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("svg"));
      element.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, img.naturalWidth);
    canvas.height = Math.max(1, img.naturalHeight);
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) throw new Error("ctx");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    return await canvasToPng(canvas);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** SVG-Platzplan einmal rastern. Liefert eine Object-URL, die der Aufrufer freigeben muss. */
export async function rasterizeSvgPlan(imageData: string, maxEdge = planRasterMaxEdge()): Promise<string> {
  const markup = svgMarkupForRaster(await svgTextFromSource(imageData), maxEdge);
  if (!/<svg\b/i.test(markup)) throw new Error("svg");
  const png = await drawSvgBlob(new Blob([markup], { type: "image/svg+xml;charset=utf-8" }));
  return URL.createObjectURL(png);
}
